import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { get } from "../api/client";

export const unreadMessagesKey = ["conversations", "unread-count"] as const;
export const missedCallsKey = ["calls", "missed-count"] as const;

/** Polls often while the tab is in front, slowly in the background (other pages also refresh it on changes). */
const badgeInterval = () => (document.visibilityState === "visible" ? 10_000 : 30_000);

/** Conversations with unread texts (key under "conversations", so reading a thread refreshes it). */
export function useUnreadMessages(enabled: boolean) {
  return useQuery({
    queryKey: unreadMessagesKey,
    queryFn: () => get<{ conversations: number }>("/conversations/unread-count"),
    enabled,
    refetchInterval: badgeInterval,
    refetchIntervalInBackground: true,
  });
}

/** Missed calls since you last opened Call history. */
export function useMissedCalls(enabled: boolean) {
  return useQuery({
    queryKey: missedCallsKey,
    queryFn: () => get<{ unseen: number }>("/calls/missed/count"),
    enabled,
    refetchInterval: badgeInterval,
    refetchIntervalInBackground: true,
  });
}

/**
 * Refreshes a badge as soon as some other polled data changes (e.g. the inbox list shows a new text),
 * so the badge and the page update together instead of waiting for the badge's own timer.
 */
export function useRefreshBadgeOnChange(data: unknown, key: readonly unknown[]) {
  const qc = useQueryClient();
  const first = useRef(true);
  useEffect(() => {
    if (data === undefined) return;
    if (first.current) { first.current = false; return; }
    void qc.invalidateQueries({ queryKey: key, exact: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key is a constant
  }, [data, qc]);
}
